import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Subject } from 'rxjs';
import moment from 'moment';
import { takeUntil } from 'rxjs/operators';
import { TransactionVM } from '../../../../../models/orderModel';
import { BackOfficeService } from '../../../../../services/backoffice.service';
import { DatePipe } from '@angular/common';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MyConfigOrganization } from '../../../../../models/myConfig';

@Component({
  selector: 'app-transaction-list',
  templateUrl: './transaction-list.component.html',
  styleUrls: ['./transaction-list.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TransactionListComponent implements OnInit, OnDestroy {
  @Output() incrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Output() decrementRequests: EventEmitter<void> = new EventEmitter<void>();
  @Input() isLoading: boolean = false;
  @Input() organization: MyConfigOrganization;

  dateFrom: string;
  dateTo: string;
  timeFrom: string;
  timeTo: string;
  transactionForm: UntypedFormGroup;
  transactions: TransactionVM[];
  dataSource: MatTableDataSource<TransactionVM> = new MatTableDataSource<TransactionVM>([]);
  barChartLabels: string[];
  barChartData: number[];
  chartLabels: string[] = ['Init'];
  chartData: number[] = [10];

  filtersExpanded: boolean = true;
  availablePaymentStatuses: string[] = [
    "PartiallyReversed",
    "Paid",
    "CHECKOUT",
    "New",
    "DELETED",
    "Captured",
    "Reversed"
  ];

  availablePaymentTypes: string[] = [
    "Swish",
    "Not yet Choosen",
    "External",
    "New",
    "CreditCard"
  ];

  displayedColumns: string[] = [
    'orderNr',
    'transactionNr',
    'amount',
    'vatAmount',
    'amountIncVat',
    'paymentStatus',
    'paymentType',
    'paymentDate',
    'debitCreditType',
    'created'
  ];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  componentDestroyed$: Subject<boolean> = new Subject();

  constructor(
    public backOfficeService: BackOfficeService,
    public dialog: MatDialog,
    public router: Router,
    private fb: UntypedFormBuilder,
    public datepipe: DatePipe,
    public snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    if (!this.organization?.organizationId) {
      this.organization = {
        ...this.organization,
        organizationId: 'a848ccd3-fd8d-4035-925e-571ebb4e93e8'
      };
    }
    this.initTransactions();
  }

  initTransactions() {
    this.dateFrom = moment().add(-10, 'd').format();
    this.timeFrom = "00:00";
    this.dateTo = moment().add(1, 'd').format();
    this.timeTo = "00:00";
    this.initTransactionForm();
    this.getTransactions();
  }

  initTransactionForm() {
    this.transactionForm = this.fb.group({
      dateFrom: [this.dateFrom],
      dateTo: [this.dateTo],
      timeFrom: [this.timeFrom],
      timeTo: [this.timeTo],
      paymentStatusFilter: [[]],  // Array for multiple selection
      paymentTypeFilter: [[]]     // Changed to array for multiple selection
    });

    // Subscribe to form changes to apply filters automatically
    this.transactionForm.valueChanges.pipe(
      takeUntil(this.componentDestroyed$)
    ).subscribe(() => {
      this.applyAdvancedFilters();
    });
  }

  toggleFilters(): void {
    this.filtersExpanded = !this.filtersExpanded;
  }

  clearFilters(): void {
    this.transactionForm.patchValue({
      paymentStatusFilter: [],
      paymentTypeFilter: []
    });
    this.applyAdvancedFilters();
  }

  applyAdvancedFilters(): void {
    if (!this.transactions) return;

    let filteredData = [...this.transactions];
    const formValues = this.transactionForm.value;

    if (formValues.paymentStatusFilter && formValues.paymentStatusFilter.length > 0) {
      filteredData = filteredData.filter(transaction =>
        formValues.paymentStatusFilter.includes(transaction.paymentStatus)
      );
    }

    if (formValues.paymentTypeFilter && formValues.paymentTypeFilter.length > 0) {
      filteredData = filteredData.filter(transaction => {
        // Handle empty payment types that should match "Not yet Choosen"
        const transactionPaymentType = transaction.paymentType || 'Not yet Choosen';
        return formValues.paymentTypeFilter.some((filterType: string) => {
          if (filterType === 'Not yet Choosen') {
            return transaction.paymentType === '' || transaction.paymentType === null || transaction.paymentType === undefined;
          } else {
            return transaction.paymentType === filterType;
          }
        });
      });
    }

    this.dataSource.data = filteredData;

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  getTransactions() {
    this.incrementRequests.emit();
    this.setDateFormats();
    this.transactions = [];
    this.backOfficeService
      .getTransactionsBetweenCreated(this.organization.organizationId, this.dateFrom, this.dateTo)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe({
        next: (transactions) => {
          this.transactions = transactions;

          // Get unique paymentStatus and paymentType values
          const uniqueStatuses = [...new Set(transactions.map(t => t.paymentStatus))];
          const uniqueTypes = [...new Set(transactions.map(t => t.paymentType))];

          console.log('Unique Payment Statuses:', uniqueStatuses);
          console.log('Unique Payment Types:', uniqueTypes);

          this.dataSource = new MatTableDataSource<TransactionVM>(this.transactions);
          this.dataSource.paginator = this.paginator;
          this.dataSource.sort = this.sort;
          this.applyAdvancedFilters();
          this.decrementRequests.emit();
          this.groupPaymentDate();
        },
        error: (error) => {
          this.decrementRequests.emit();
          this.snackBar.open(`Felkod: ${error.status}`, 'Doh!', {
            verticalPosition: 'top'
          });
          console.error('TransactionListComponent - Error in getTransactions', error);
        }
      });
  }

  groupPaymentDate() {
    this.barChartLabels = [];
    this.barChartData = [];

    var groupedTransactions = this.transactions.reduceRight((p, c) => {
      var paymentDate = moment(c.paymentDate).format('YYYY-MM-DD')
      if (!p.hasOwnProperty(paymentDate)) {
        p[paymentDate] = 0;
      }
      p[paymentDate] = p[paymentDate] + c.amount;
      return p;
    }, {});

    for (const transaction in groupedTransactions) {
      this.barChartLabels.push(transaction);
      this.barChartData.push(groupedTransactions[transaction]);
    }

    this.chartLabels = this.barChartLabels;
    this.chartData = this.barChartData;
  }

  setDateFormats() {
    this.dateFrom = this.datepipe.transform(this.transactionForm.controls['dateFrom'].value, 'yyyy-MM-dd') + ' ' + this.transactionForm.controls['timeFrom'].value;
    this.dateTo = this.datepipe.transform(this.transactionForm.controls['dateTo'].value, 'yyyy-MM-dd') + ' ' + this.transactionForm.controls['timeTo'].value;
  }

  goToOrder(orderId: string): void {
    const url = this.router.serializeUrl(this.router.createUrlTree([`/admin/back-office24-order/${orderId}`]));
    window.open(url, '_blank');
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  flatten(obj: any) {
    return Object.keys(obj).reduce((acc, current) => {
      const _key = `${current}`;
      const currentValue = obj[current];
      if (Array.isArray(currentValue) || Object(currentValue) === currentValue) {
        Object.assign(acc, this.flatten(currentValue));
      } else {
        acc[_key] = currentValue;
      }
      return acc;
    }, {});
  }

  ngOnDestroy() {
    this.componentDestroyed$.next(true);
    this.componentDestroyed$.complete();
  }
}
